import React from "react";
import { API_BASE } from "@/lib/api";
import { logger } from "@/lib/logger";

const WS_BASE = API_BASE.replace(/^http/, "ws");
const PING_INTERVAL_MS = 25000;
const RECONNECT_DELAY_MS = 3000;

// -------- message handlers --------
// Each returns a partial-state updater (or performs a side effect via setters).

function handleInit(msg, setters) {
  setters.setSelfInfo(msg.self);
  setters.setPeers(msg.peers || []);
}

function handleJoin(msg, setters) {
  setters.setPeers((prev) => [...prev.filter((x) => x.id !== msg.user.id), msg.user]);
}

function handleLeave(msg, setters) {
  setters.setCursors((prev) => {
    const next = { ...prev };
    delete next[msg.client_id];
    return next;
  });
}

function handleCursor(msg, setters) {
  setters.setCursors((prev) => ({ ...prev, [msg.client_id]: msg }));
}

function handleComment(msg, setters) {
  setters.setComments((prev) => [msg.comment, ...prev]);
}

const HANDLERS = {
  init: handleInit,
  join: handleJoin,
  leave: handleLeave,
  cursor: handleCursor,
  comment: handleComment,
};

function newClientId() {
  return `c-${Math.random().toString(36).slice(2, 10)}`;
}

// -------- hook --------

export function useProjectRealtime(projectId) {
  const [peers, setPeers] = React.useState([]);
  const [cursors, setCursors] = React.useState({});
  const [comments, setComments] = React.useState([]);
  const [selfInfo, setSelfInfo] = React.useState(null);
  const [connected, setConnected] = React.useState(false);
  const wsRef = React.useRef(null);
  const clientIdRef = React.useRef(null);

  React.useEffect(() => {
    if (!projectId) return undefined;
    const token = localStorage.getItem("sf_token");
    if (!token) return undefined;
    if (!clientIdRef.current) clientIdRef.current = newClientId();

    const url = `${WS_BASE}/ws/project/${projectId}?token=${encodeURIComponent(token)}&client_id=${clientIdRef.current}`;
    let stopped = false;
    let pingTimer = null;
    const setters = { setPeers, setCursors, setComments, setSelfInfo };

    const onMessage = (evt) => {
      let msg;
      try {
        msg = JSON.parse(evt.data);
      } catch (err) {
        logger.warn("realtime: bad message payload", err);
        return;
      }
      const handler = HANDLERS[msg.type];
      if (handler) handler(msg, setters);
    };

    const connect = () => {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
        }, PING_INTERVAL_MS);
      };
      ws.onmessage = onMessage;
      ws.onerror = (err) => logger.warn("realtime: ws error", err);
      ws.onclose = () => {
        setConnected(false);
        if (pingTimer) clearInterval(pingTimer);
        if (!stopped) setTimeout(connect, RECONNECT_DELAY_MS);
      };
    };
    connect();

    return () => {
      stopped = true;
      if (pingTimer) clearInterval(pingTimer);
      if (wsRef.current) wsRef.current.close();
    };
  }, [projectId]);

  const sendCursor = React.useCallback((nodeIndex, selection) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "cursor", node_index: nodeIndex, selection }));
    }
  }, []);

  const sendComment = React.useCallback((targetType, targetId, body) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "comment", target_type: targetType, target_id: targetId, body }));
    }
  }, []);

  return { peers, cursors, comments, setComments, selfInfo, connected, sendCursor, sendComment };
}
